import React, { ReactElement, useEffect, useState } from 'react';
import detectBrowserLanguage from 'detect-browser-language';
import { Skeleton } from 'primereact/skeleton';
import Notification from '@Common/Notification';
import { useValidateTranslation, useTranslateText } from '@Apis/locationTasks';
import { LocationTaskLabel, LocationTaskTranslation } from './Types';

interface TranslateProps {
    locationTaskId: number;
    translationLabels: LocationTaskLabel;
    setTranslationLabels: React.Dispatch<React.SetStateAction<LocationTaskLabel>>;  // Parent component also needs to know the labels for translation so we use it's state instead of ours
    translation: LocationTaskTranslation | null;
    setTranslation: React.Dispatch<React.SetStateAction<LocationTaskTranslation | null>>;   // Parent component needs to render the translated task, so we use it's state instead of ours
    newDesign?: boolean;
}

const Translate = (props: TranslateProps): ReactElement => {

    const { locationTaskId, translationLabels, setTranslationLabels, translation, setTranslation, newDesign } = props;

    const [userLanguage, setUserLanguage] = useState<string>('');
    const [canTranslate, setCanTranslate] = useState<boolean>(false);

    const validateMutation = useValidateTranslation((result) => {
        if (result) {
            setCanTranslate(result.canTranslate === true);
            if (result.labels) {
                setTranslationLabels(result.labels as LocationTaskLabel);
            }
        }
    });

    const translateMutation = useTranslateText((result) => {
        if (result?.locationTask) {
            Notification('success', '', 'The location task was translated');
            setTranslation(result.locationTask as LocationTaskTranslation);
        }
    });

    const isLoading = validateMutation.isPending;
    const isTranslating = translateMutation.isPending;

    const validateCanTranslate = () => {
        const browserLanguage = detectBrowserLanguage();
        setUserLanguage(browserLanguage);
        validateMutation.mutate(
            { locationTaskId, language: browserLanguage },
            {
                onError: () => setCanTranslate(false),
            }
        );
    };

    const onLocationTaskTranslation = () => {
        if (isTranslating) return;
        const request = { locationTaskId, language: userLanguage };
        translateMutation.mutate(request, {
            onError: (error) => {
                Notification('error', 'Error', (error as Error).message || 'Translation failed');
            },
        });
    };

    useEffect(() => {
        if (locationTaskId) {
            validateCanTranslate();
        }
    }, [locationTaskId]);

    return (
        <>
            {
                (newDesign ?
                    isLoading
                        ? <Skeleton width='15%' height='100%' className='' />
                        : !translation && canTranslate &&
                        <button
                            type='button'
                            title={translationLabels.translateLabel}
                            disabled={isTranslating}
                            onClick={onLocationTaskTranslation}
                            className={`action-button${isTranslating ? ' disabled' : ''}`}
                        >
                            {isTranslating
                                ? <i className='fa-thin fa-gear fa-spin action-icon'></i>
                                : <i className='fa-thin fa-globe action-icon'></i>
                            }
                            <span className='action-label'>{translationLabels.translateLabel}</span>
                        </button>
                    : isLoading
                        ? <Skeleton width='100%' height='100%' className='' />
                        : !translation && canTranslate &&
                        <div className='link-with-icon'>
                            {isTranslating
                                ? <i className={'fa-thin fa-gear fa-spin'}></i>
                                : <i className={'fa-thin fa-globe'}></i>
                            }
                            <button type='button' title={translationLabels.translateLabel} className={`btn btn-link ${isTranslating ? 'disabled' : ''}`} onClick={onLocationTaskTranslation}>
                                {translationLabels.translateLabel}
                            </button>
                        </div>
                )
            }
        </>
    );
};

export default Translate;
